import styled from '@emotion/styled';
import React, { useCallback } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Employee } from '../..';
import {
  CancelButton,
  Input,
  PolarityInput,
  SubmitButton,
} from '../../../../../../components/form';
import { useMutationErrorHandler } from '../../../../../../hooks/useMutationErrorHandler';
import CardHeader from '../../../../components/CardHeader';
import {
  ManagerEmployeeNotePolarity,
  useEmployeePageCreateEmployeeNoteMutation,
} from '../../../../__generated__/graphql';

type Props = {
  employee: Employee;
};

type FormData = {
  text: string;
  polarity: ManagerEmployeeNotePolarity;
};

const validationSchema: yup.ObjectSchema<FormData> = yup.object({
  text: yup.string().required(),
  polarity: yup.mixed<ManagerEmployeeNotePolarity>().required(),
});

const defaultValues = {
  text: '',
  polarity: ManagerEmployeeNotePolarity.Neutral,
};

const CardFooter = styled(Card.Footer)({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  '> button:not(:first-of-type)': {
    marginLeft: '0.5rem',
  },
});

const InputContainer: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => (
  <Row>
    <Col>{children}</Col>
  </Row>
);

export const EmployeeNoteForm: React.FC<React.PropsWithChildren<Props>> = ({
  employee: { Id: employeeId, Name: employeeName },
}) => {
  const { t } = useTranslation();
  const [createNote] = useEmployeePageCreateEmployeeNoteMutation();
  const errorHandler = useMutationErrorHandler();

  const form = useForm<FormData>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = useCallback(
    async (data: FormData) => {
      await createNote({
        variables: {
          input: {
            employeeId,
            text: data.text,
            polarity: data.polarity,
          },
          employeeId,
        },
      })
        .then(() => form.reset())
        .catch(errorHandler);
    },
    [createNote, employeeId, form, errorHandler],
  );

  return (
    <Card>
      <CardHeader>
        {t('managerEmployeeNotes.form.cardTitle', {
          employee: employeeName,
        })}
      </CardHeader>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Card.Body>
            <InputContainer>
              <Input
                name="text"
                type="textarea"
                className="form-control-flush"
                placeholder={t(
                  'managerEmployeeNotes.form.textInput.placeholder',
                )}
                labelComponent={<></>}
              />
            </InputContainer>
            <InputContainer>
              <PolarityInput name="polarity" />
            </InputContainer>
          </Card.Body>

          <CardFooter>
            <SubmitButton size="sm">
              {t('form.general.saveButton')}
            </SubmitButton>
            <CancelButton size="sm">
              {t('form.general.cancelButton')}
            </CancelButton>
          </CardFooter>
        </form>
      </FormProvider>
    </Card>
  );
};
